import { trpc } from '@/lib/trpc';
import { Card, CardContent } from '@/components/ui/Card';

interface BalanceteViewProps {
  eventoId: string;
}

export function BalanceteView({ eventoId }: BalanceteViewProps) {
  const { data: balancete, isLoading } = trpc.livroCaixa.getBalancete.useQuery({ eventoId });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  if (!balancete) return null;

  return (
    <div className="space-y-6">
      {/* Receitas */}
      <Card className="border border-gray-200">
        <CardContent className="p-0">
          <div className="bg-green-50 px-4 py-3 border-b border-green-200">
            <h3 className="font-semibold text-green-800">Receitas do Evento</h3>
          </div>
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 w-10"></th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Conta</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Total</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 w-20">%</th>
              </tr>
            </thead>
            <tbody>
              {balancete.receitas.map((r) => (
                <tr key={r.codigo} className="border-b border-gray-50">
                  <td className="px-4 py-2 text-sm text-green-600 font-medium">(+)</td>
                  <td className="px-4 py-2 text-sm text-gray-900">{r.conta}</td>
                  <td className="px-4 py-2 text-sm text-right text-gray-900">{formatCurrency(r.total)}</td>
                  <td className="px-4 py-2 text-sm text-right text-gray-500">{r.percentual.toFixed(1)}%</td>
                </tr>
              ))}
              <tr className="bg-green-50 font-semibold">
                <td className="px-4 py-3"></td>
                <td className="px-4 py-3 text-sm text-green-800">Total de Receitas</td>
                <td className="px-4 py-3 text-sm text-right text-green-800">{formatCurrency(balancete.totalReceitas)}</td>
                <td className="px-4 py-3 text-sm text-right text-green-800">100%</td>
              </tr>
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Despesas */}
      <Card className="border border-gray-200">
        <CardContent className="p-0">
          <div className="bg-red-50 px-4 py-3 border-b border-red-200">
            <h3 className="font-semibold text-red-800">Despesas do Evento</h3>
          </div>
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 w-10"></th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Conta</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Total</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 w-20">%</th>
              </tr>
            </thead>
            <tbody>
              {balancete.despesas.map((d) => (
                <tr key={d.codigo} className="border-b border-gray-50">
                  <td className="px-4 py-2 text-sm text-red-600 font-medium">(-)</td>
                  <td className="px-4 py-2 text-sm text-gray-900">{d.conta}</td>
                  <td className="px-4 py-2 text-sm text-right text-gray-900">{formatCurrency(d.total)}</td>
                  <td className="px-4 py-2 text-sm text-right text-gray-500">{d.percentual.toFixed(1)}%</td>
                </tr>
              ))}
              <tr className="bg-red-50 font-semibold">
                <td className="px-4 py-3"></td>
                <td className="px-4 py-3 text-sm text-red-800">Total de Despesas</td>
                <td className="px-4 py-3 text-sm text-right text-red-800">{formatCurrency(balancete.totalDespesas)}</td>
                <td className="px-4 py-3 text-sm text-right text-red-800">100%</td>
              </tr>
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Resultado */}
      <Card className="border-2 border-gray-300">
        <CardContent className="p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Resultado</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-gray-600">Total de Receitas</span>
              <span className="font-semibold text-green-600">{formatCurrency(balancete.totalReceitas)}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-gray-600">Total de Despesas</span>
              <span className="font-semibold text-red-600">{formatCurrency(balancete.totalDespesas)}</span>
            </div>
            <div className="flex items-center justify-between py-3">
              <span className="text-lg font-bold text-gray-900">Saldo Final do Evento</span>
              <span className={`text-2xl font-bold ${balancete.saldo >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(balancete.saldo)}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
